import type { QuestView } from '@shared/types/quest';
import { QUEST_DEFINITIONS } from '@shared/data/quests';
import { getItemTemplate } from '@shared/data/items';
import type { WorldMapIndex, WorldMapAreaEntry } from '@shared/utils/worldMapIndex';
import type { MapMarker } from '../MapMarkers';
import { createTextInput } from '../../../ui/formControls';
import { attachCustomScrollbar } from '../../../ui/customScrollbar';

/**
 * Dock lateral derecho del World Map (tecla M). Panel DOM (fixed) por encima del
 * canvas, con tabs Áreas / Ciudades / Quests / Buscar, barra de filtros/legenda y
 * listas navegables. Reutiliza `formControls` (input), `customScrollbar` e íconos
 * del atlas (`applyIconToDom`). Todo el render del mapa (pins/áreas/rutas) es
 * Phaser y vive en `MapOverlay`; el dock sólo dispara callbacks. Ver docs/WORLD_MAP.md.
 */

export interface WorldMapFilters {
  areas: boolean;
  neutrals: boolean;
  hostiles: boolean;
  bosses: boolean;
  vendors: boolean;
  quests: boolean;
  resources: boolean;
}

export const DEFAULT_FILTERS: WorldMapFilters = {
  areas: true,
  neutrals: true,
  hostiles: true,
  bosses: true,
  vendors: true,
  quests: true,
  resources: true,
};

export interface WorldMapDockCallbacks {
  /** Centrar + zoom en un tile del mapa. */
  onFlyToTile: (x: number, y: number) => void;
  /** Seleccionar (resaltar) un área y volar a ella. */
  onSelectArea: (areaId: string) => void;
  /** Trackear una quest (dibuja su ruta) o null para desactivar. */
  onTrackQuest: (questId: string | null) => void;
  /** Mostrar la tarjeta de un NPC (por templateId). */
  onShowNpcTemplate: (templateId: string) => void;
  /** Los filtros de capa cambiaron. */
  onFiltersChange: (f: WorldMapFilters) => void;
  /** Entrar en modo "colocar marcador" (después se clickea el mapa para crearlo). */
  onCreateMarker: () => void;
  /** Volar/centrar a un marcador por id. */
  onFlyToMarker: (id: string) => void;
  /** Renombrar un marcador por id (abre prompt). */
  onRenameMarker: (id: string) => void;
  /** Borrar un marcador por id. */
  onDeleteMarker: (id: string) => void;
}

export interface WorldMapDockData {
  index: WorldMapIndex;
  getActiveQuests: () => QuestView[];
  /** IDs de quests ya entregadas (para pintar el catálogo del tab Quests). */
  getCompletedQuestIds: () => ReadonlySet<string>;
  /** Nivel del player local (para gatear por color el catálogo de quests). */
  getPlayerLevel: () => number;
  /** Marcadores del personaje actual (lista viva; el dock la re-lee en refreshMarkers). */
  getMarkers: () => MapMarker[];
  /** Quest trackeada al abrir (fuente única = favorita de QuestUI). Pinta activa su fila. */
  initialTrackedQuestId?: string | null;
}

/** Filtro activo del tab Quests: todas / sólo activas / sólo completadas. */
type QuestFilter = 'all' | 'active' | 'completed';

type TabId = 'areas' | 'cities' | 'quests' | 'search';

const STYLE_ID = 'wmd-style';

export class WorldMapDock {
  private readonly cb: WorldMapDockCallbacks;
  private readonly data: WorldMapDockData;
  private root: HTMLDivElement | null = null;
  private bodyEl: HTMLDivElement | null = null;
  private markersListEl: HTMLDivElement | null = null;
  private tab: TabId = 'cities';
  private filters: WorldMapFilters = { ...DEFAULT_FILTERS };
  private trackedQuestId: string | null = null;
  private searchQuery = '';
  /** Estado del tab Quests: búsqueda por nombre + filtro activas/completadas. */
  private questSearchQuery = '';
  private questFilter: QuestFilter = 'all';

  constructor(cb: WorldMapDockCallbacks, data: WorldMapDockData) {
    this.cb = cb;
    this.data = data;
    this.trackedQuestId = data.initialTrackedQuestId ?? null;
  }

  getFilters(): WorldMapFilters {
    return this.filters;
  }

  show(): void {
    if (this.root) return;
    ensureStyle();
    this.build();
  }

  hide(): void {
    this.root?.remove();
    this.root = null;
    this.bodyEl = null;
    this.markersListEl = null;
  }

  /** Refresca el contenido (p.ej. cambió el estado de quests). */
  refresh(): void {
    if (this.root) this.renderBody();
  }

  /** Re-renderiza sólo la lista de marcadores (tras crear/renombrar/borrar). */
  refreshMarkers(): void {
    if (this.markersListEl) this.renderMarkers();
  }

  /** Fundido del dock por transición CSS (0 = cerrado, 1 = abierto). */
  fadeTo(opacity: number, ms: number): void {
    const r = this.root;
    if (!r) return;
    r.style.transition = `opacity ${ms}ms ease`;
    r.style.opacity = String(opacity);
  }

  // ── Construcción ─────────────────────────────────────────────────────────
  private build(): void {
    const root = document.createElement('div');
    root.className = 'wmd-root';
    root.addEventListener('keydown', (e) => e.stopPropagation());
    root.addEventListener('wheel', (e) => e.stopPropagation());

    // Header
    const header = document.createElement('div');
    header.className = 'wmd-header';
    const title = document.createElement('div');
    title.className = 'wmd-title';
    title.textContent = 'MAPA DEL MUNDO';
    header.appendChild(title);
    root.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'wmd-tabs';
    const tabDefs: Array<{ id: TabId; label: string }> = [
      { id: 'cities', label: 'Ciudades' },
      { id: 'areas', label: 'Áreas' },
      { id: 'quests', label: 'Quests' },
      { id: 'search', label: 'Buscar' },
    ];
    for (const t of tabDefs) {
      const el = document.createElement('button');
      el.className = 'wmd-tab' + (this.tab === t.id ? ' wmd-tab--active' : '');
      el.textContent = t.label;
      el.onclick = () => {
        this.tab = t.id;
        for (const c of Array.from(tabs.children)) c.classList.remove('wmd-tab--active');
        el.classList.add('wmd-tab--active');
        this.renderBody();
      };
      tabs.appendChild(el);
    }
    root.appendChild(tabs);

    // Filtros / legenda
    root.appendChild(this.buildFilterBar());

    // Body (contenido de la tab)
    const body = document.createElement('div');
    body.className = 'wmd-body';
    this.bodyEl = body;
    root.appendChild(body);
    attachCustomScrollbar(body);

    // Sección fija de MARCADORES abajo del todo (fuera del body scrolleable).
    root.appendChild(this.buildMarkersSection());

    // Arranca invisible; MapOverlay dispara el fundido (fadeTo) al abrir. El reflow
    // asegura que el 0 quede committeado para que la transición a 1 anime.
    root.style.opacity = '0';
    document.body.appendChild(root);
    void root.offsetHeight;
    this.root = root;
    this.renderBody();
  }

  private buildFilterBar(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.className = 'wmd-filters';
    const defs: Array<{ key: keyof WorldMapFilters; label: string; icon?: string; swatch?: string }> = [
      { key: 'areas', label: 'Áreas', swatch: '#6aa9ff' },
      { key: 'neutrals', label: 'NPCs', swatch: '#ffd94a' },
      { key: 'hostiles', label: 'Hostiles', swatch: '#e8564a' },
      { key: 'bosses', label: 'Bosses', swatch: '#ffd56b' },
      { key: 'vendors', label: 'Tiendas', swatch: '#7ad07a' },
      { key: 'quests', label: 'Quests', swatch: '#c79bff' },
      { key: 'resources', label: 'Recursos', swatch: '#e0a13c' },
    ];
    for (const d of defs) {
      const chip = document.createElement('button');
      chip.className = 'wmd-chip' + (this.filters[d.key] ? ' wmd-chip--on' : '');
      const sw = document.createElement('span');
      sw.className = 'wmd-chip-sw';
      if (d.swatch) sw.style.background = d.swatch;
      chip.appendChild(sw);
      const lbl = document.createElement('span');
      lbl.textContent = d.label;
      chip.appendChild(lbl);
      chip.onclick = () => {
        this.filters[d.key] = !this.filters[d.key];
        chip.classList.toggle('wmd-chip--on', this.filters[d.key]);
        this.cb.onFiltersChange({ ...this.filters });
      };
      bar.appendChild(chip);
    }
    return bar;
  }

  // ── Render de la tab activa ───────────────────────────────────────────────
  private renderBody(): void {
    const body = this.bodyEl;
    if (!body) return;
    body.innerHTML = '';
    switch (this.tab) {
      case 'cities':
        this.renderAreaList(body, this.data.index.cities, 'No hay ciudades en este mapa.');
        break;
      case 'areas':
        this.renderAreaList(body, this.data.index.zones, 'No hay zonas con nombre.');
        break;
      case 'quests':
        this.renderQuests(body);
        break;
      case 'search':
        this.renderSearch(body);
        break;
    }
  }

  private renderAreaList(body: HTMLElement, areas: WorldMapAreaEntry[], emptyMsg: string): void {
    if (areas.length === 0) {
      body.appendChild(this.emptyEl(emptyMsg));
      return;
    }
    for (const a of areas) {
      const row = document.createElement('div');
      row.className = 'wmd-row';
      const dot = document.createElement('span');
      dot.className = 'wmd-row-dot';
      dot.style.background = ZONE_COLOR[a.zoneType] ?? '#888';
      row.appendChild(dot);

      const main = document.createElement('div');
      main.className = 'wmd-row-main';
      const name = document.createElement('div');
      name.className = 'wmd-row-name';
      name.textContent = a.name;
      main.appendChild(name);

      const info = this.data.index.areaNpcs.get(a.id);
      const bits: string[] = [];
      if (info && info.neutrals.length) bits.push(`${info.neutrals.length} NPC`);
      if (info && info.hostileTotal) bits.push(`☠ ${info.hostileTotal}`);
      const sub = document.createElement('div');
      sub.className = 'wmd-row-sub';
      sub.textContent = bits.join('  ·  ') || ZONE_LABEL[a.zoneType] || '';
      main.appendChild(sub);
      row.appendChild(main);

      row.onclick = () => this.cb.onSelectArea(a.id);
      body.appendChild(row);
    }
  }

  /**
   * Tab Quests = planificador del CATÁLOGO completo (no sólo activas): todas las quests
   * ordenadas por nivel requerido asc, con buscador + dos filtros excluyentes
   * (Activadas / Completadas). Coloreo por gating de nivel (te da el nivel y no la hiciste
   * = normal; no llegás = atenuada; completada = atenuada + ✓). Click en una fila vuela al
   * NPC que inicia la quest y abre su tarjeta. Ver docs/WORLD_MAP.md.
   */
  private renderQuests(body: HTMLElement): void {
    // --- Buscador ---
    const { input, wrapper } = createTextInput({
      type: 'search',
      placeholder: 'Buscar misión…',
      value: this.questSearchQuery,
    });
    wrapper.classList.add('wmd-search');
    body.appendChild(wrapper);

    // --- Filtros excluyentes: Activadas / Completadas (re-click = volver a "todas") ---
    const toggles = document.createElement('div');
    toggles.className = 'wmd-qtoggles';
    const listHost = document.createElement('div');
    listHost.className = 'wmd-qlist';

    const mkToggle = (label: string, key: 'active' | 'completed'): HTMLButtonElement => {
      const b = document.createElement('button');
      b.className = 'wmd-qtoggle' + (this.questFilter === key ? ' wmd-qtoggle--on' : '');
      b.textContent = label;
      b.onclick = () => {
        this.questFilter = this.questFilter === key ? 'all' : key;
        for (const c of Array.from(toggles.children)) c.classList.remove('wmd-qtoggle--on');
        if (this.questFilter === key) b.classList.add('wmd-qtoggle--on');
        this.renderQuestList(listHost);
      };
      return b;
    };
    toggles.appendChild(mkToggle('Activadas', 'active'));
    toggles.appendChild(mkToggle('Completadas', 'completed'));
    body.appendChild(toggles);

    body.appendChild(listHost);

    input.addEventListener('input', () => {
      this.questSearchQuery = input.value;
      this.renderQuestList(listHost);
    });
    this.renderQuestList(listHost);
  }

  /** (Re)construye SÓLO la lista de quests del catálogo según búsqueda + filtro. */
  private renderQuestList(host: HTMLElement): void {
    host.innerHTML = '';
    const playerLevel = this.data.getPlayerLevel();
    const completed = this.data.getCompletedQuestIds();
    const activeById = new Map(this.data.getActiveQuests().map((q) => [q.id, q] as const));
    const q = this.questSearchQuery.trim().toLowerCase();

    // Catálogo completo ordenado por nivel requerido asc (más bajo arriba).
    const defs = Object.values(QUEST_DEFINITIONS).slice().sort(
      (a, b) => (a.requirements?.minLevel ?? 0) - (b.requirements?.minLevel ?? 0)
    );

    let shown = 0;
    for (const def of defs) {
      const activeView = activeById.get(def.id);
      const status: 'completed' | 'ready' | 'active' | 'available' = completed.has(def.id)
        ? 'completed'
        : activeView
          ? activeView.status === 'ready'
            ? 'ready'
            : 'active'
          : 'available';

      // Filtro excluyente.
      if (this.questFilter === 'active' && !(status === 'active' || status === 'ready')) continue;
      if (this.questFilter === 'completed' && status !== 'completed') continue;
      // Filtro por búsqueda (por nombre).
      if (q && !def.nameEs.toLowerCase().includes(q)) continue;

      const minLevel = def.requirements?.minLevel ?? 0;
      const meetsLevel = playerLevel >= minLevel;
      const locked = status === 'available' && !meetsLevel;

      const row = document.createElement('div');
      row.className =
        'wmd-qrow2' +
        (status === 'completed' ? ' wmd-qrow2--done' : locked ? ' wmd-qrow2--locked' : '');

      // Chip de nivel a la izquierda.
      const lvl = document.createElement('span');
      lvl.className = 'wmd-qrow2-lvl';
      lvl.textContent = minLevel > 0 ? `Nv ${minLevel}` : 'Nv 1';
      row.appendChild(lvl);

      const main = document.createElement('div');
      main.className = 'wmd-qrow2-main';
      const name = document.createElement('div');
      name.className = 'wmd-qrow2-name';
      name.textContent = def.nameEs;
      main.appendChild(name);
      const sub = document.createElement('div');
      sub.className = 'wmd-qrow2-sub';
      sub.textContent = QUEST_STATUS_LABEL[status] + (locked ? `  ·  requiere nivel ${minLevel}` : '');
      main.appendChild(sub);
      row.appendChild(main);

      // Ubicación del NPC que inicia la quest (para volar + seleccionar).
      const giver = this.data.index.npcsByTemplate.get(def.giverNpcId)?.[0] ?? null;
      row.onclick = () => {
        if (giver) this.cb.onFlyToTile(giver.x, giver.y);
        this.cb.onShowNpcTemplate(def.giverNpcId);
      };
      host.appendChild(row);
      shown++;
    }

    if (shown === 0) {
      host.appendChild(
        this.emptyEl(
          this.questFilter === 'active'
            ? 'No tenés misiones activas.'
            : this.questFilter === 'completed'
              ? 'Todavía no completaste misiones.'
              : 'No hay misiones que coincidan.'
        )
      );
    }
  }

  private renderSearch(body: HTMLElement): void {
    const { input, wrapper } = createTextInput({ type: 'search', placeholder: 'NPC, ítem o zona…', value: this.searchQuery });
    wrapper.classList.add('wmd-search');
    body.appendChild(wrapper);
    const results = document.createElement('div');
    results.className = 'wmd-results';
    body.appendChild(results);

    const run = (): void => {
      this.searchQuery = input.value;
      this.renderSearchResults(results, input.value.trim().toLowerCase());
    };
    input.addEventListener('input', run);
    // Ejecutar de una si venía con query.
    if (this.searchQuery) run();
    else this.renderSearchResults(results, '');
    setTimeout(() => input.focus(), 0);
  }

  private renderSearchResults(host: HTMLElement, q: string): void {
    host.innerHTML = '';
    if (q.length < 2) {
      host.appendChild(this.emptyEl('Escribí al menos 2 letras.'));
      return;
    }
    const idx = this.data.index;
    let count = 0;
    const MAX = 40;

    // Áreas por nombre.
    for (const a of idx.areas) {
      if (count >= MAX) break;
      if (a.name.toLowerCase().includes(q)) {
        host.appendChild(this.resultRow('📍', a.name, ZONE_LABEL[a.zoneType], () => this.cb.onSelectArea(a.id)));
        count++;
      }
    }
    // NPCs por nombre (dedupe por template).
    const seen = new Set<string>();
    for (const n of [...idx.neutrals, ...idx.hostiles]) {
      if (count >= MAX) break;
      if (seen.has(n.templateId)) continue;
      if (n.name.toLowerCase().includes(q)) {
        seen.add(n.templateId);
        const sub = n.hostile ? 'Hostil' : 'NPC';
        host.appendChild(this.resultRow(n.hostile ? '☠' : '🙂', n.name, sub, () => {
          this.cb.onFlyToTile(n.x, n.y);
          this.cb.onShowNpcTemplate(n.templateId);
        }));
        count++;
      }
    }
    // Yacimientos por nombre (dedupe por tipo, ir al más cercano no aplica: al primero).
    const seenNode = new Set<string>();
    for (const n of idx.resourceNodes) {
      if (count >= MAX) break;
      if (seenNode.has(n.typeId)) continue;
      if (!n.name.toLowerCase().includes(q)) continue;
      seenNode.add(n.typeId);
      const skillLabel = n.skill === 'skill_mining' ? 'Minería' : 'Leñería';
      host.appendChild(this.resultRow('⛏', n.name, `${skillLabel} nivel ${n.requiredLevel}`, () => this.cb.onFlyToTile(n.x, n.y)));
      count++;
    }
    // Ítems → NPCs que lo dropean.
    for (const [itemId, tplIds] of idx.dropIndex) {
      if (count >= MAX) break;
      const item = getItemTemplate(itemId);
      const itemName = item?.name ?? itemId;
      if (!itemName.toLowerCase().includes(q)) continue;
      for (const tplId of tplIds) {
        if (count >= MAX) break;
        const placements = idx.npcsByTemplate.get(tplId);
        const n = placements && placements[0];
        if (!n) continue;
        host.appendChild(this.resultRow('🎁', `${itemName}`, `lo suelta ${n.name}`, () => {
          this.cb.onFlyToTile(n.x, n.y);
          this.cb.onShowNpcTemplate(tplId);
        }));
        count++;
      }
    }

    if (count === 0) host.appendChild(this.emptyEl('Sin resultados.'));
  }

  private resultRow(icon: string, name: string, sub: string, onClick: () => void): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'wmd-row';
    const ic = document.createElement('span');
    ic.className = 'wmd-row-ic';
    ic.textContent = icon;
    row.appendChild(ic);
    const main = document.createElement('div');
    main.className = 'wmd-row-main';
    const nm = document.createElement('div');
    nm.className = 'wmd-row-name';
    nm.textContent = name;
    const sb = document.createElement('div');
    sb.className = 'wmd-row-sub';
    sb.textContent = sub;
    main.appendChild(nm);
    main.appendChild(sb);
    row.appendChild(main);
    row.onclick = onClick;
    return row;
  }

  private emptyEl(msg: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'wmd-empty';
    el.textContent = msg;
    return el;
  }

  // ── Marcadores (sección fija abajo) ───────────────────────────────────────
  private buildMarkersSection(): HTMLDivElement {
    const sec = document.createElement('div');
    sec.className = 'wmd-markers';

    const head = document.createElement('div');
    head.className = 'wmd-markers-head';
    const t = document.createElement('span');
    t.textContent = 'MARCADORES';
    head.appendChild(t);
    const create = document.createElement('button');
    create.className = 'wmd-mk-create';
    create.textContent = '+ Crear';
    create.onclick = () => this.cb.onCreateMarker();
    head.appendChild(create);
    sec.appendChild(head);

    const list = document.createElement('div');
    list.className = 'wmd-markers-list';
    this.markersListEl = list;
    attachCustomScrollbar(list);
    sec.appendChild(list);

    this.renderMarkers();
    return sec;
  }

  private renderMarkers(): void {
    const list = this.markersListEl;
    if (!list) return;
    list.innerHTML = '';
    const markers = this.data.getMarkers();
    if (markers.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'wmd-mk-empty';
      empty.textContent = 'Sin marcadores. Tocá "+ Crear" y clickeá el mapa.';
      list.appendChild(empty);
      return;
    }
    for (const m of markers) {
      const row = document.createElement('div');
      row.className = 'wmd-mk-row';

      const dot = document.createElement('span');
      dot.className = 'wmd-mk-dot';
      dot.style.background = colorToCss(m.color);
      row.appendChild(dot);

      const main = document.createElement('div');
      main.className = 'wmd-mk-main';
      const name = document.createElement('div');
      name.className = 'wmd-mk-name';
      name.textContent = m.label;
      const sub = document.createElement('div');
      sub.className = 'wmd-mk-sub';
      sub.textContent = `x:${m.tileX}  y:${m.tileY}`;
      main.appendChild(name);
      main.appendChild(sub);
      main.onclick = () => this.cb.onFlyToMarker(m.id);
      row.appendChild(main);

      const edit = document.createElement('button');
      edit.className = 'wmd-mk-btn';
      edit.textContent = '✏';
      edit.title = 'Renombrar';
      edit.onclick = (e) => {
        e.stopPropagation();
        this.cb.onRenameMarker(m.id);
      };
      row.appendChild(edit);

      const del = document.createElement('button');
      del.className = 'wmd-mk-btn wmd-mk-btn--del';
      del.textContent = '✕';
      del.title = 'Borrar';
      del.onclick = (e) => {
        e.stopPropagation();
        this.cb.onDeleteMarker(m.id);
      };
      row.appendChild(del);

      list.appendChild(row);
    }
  }
}

/** Color numérico de Phaser (0xRRGGBB) → css `#rrggbb`. */
function colorToCss(n: number): string {
  return '#' + (n & 0xffffff).toString(16).padStart(6, '0');
}

const ZONE_COLOR: Record<string, string> = {
  safe: '#5fbf6a',
  neutral: '#c9b45f',
  especial: '#d6a83f',
  danger: '#d6564a',
};
const ZONE_LABEL: Record<string, string> = {
  safe: 'Zona segura',
  neutral: 'Territorio de facción',
  especial: 'Peligrosa (sin drop)',
  danger: 'Zona peligrosa',
};
/** Etiqueta corta por estado de quest (fila del catálogo del tab Quests). */
const QUEST_STATUS_LABEL: Record<'completed' | 'ready' | 'active' | 'available', string> = {
  completed: '✓ Completada',
  ready: '¡Listo para entregar!',
  active: 'En curso',
  available: 'Disponible',
};

// ── CSS (inyectado una vez) ──────────────────────────────────────────────────
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  // Tipografías al DOBLE (pedido de Dalmiro): fuentes 11-18px → 22-36px y el dock ensanchado
  // 300 → 560px con paddings/altos escalados para que no se desborde. Ver docs/WORLD_MAP.md.
  style.textContent = `
  .wmd-root{position:fixed;top:0;right:0;width:560px;height:100vh;z-index:2200;
    background:rgba(16,15,12,0.94);border-left:2px solid #8a7a4a;color:#e8e0cc;
    font-family:'Monogram',monospace;display:flex;flex-direction:column;box-shadow:-6px 0 20px rgba(0,0,0,0.5);}
  .wmd-header{padding:18px 20px 12px;border-bottom:1px solid #3a3222;}
  .wmd-title{font-size:36px;color:#ffe9a8;letter-spacing:2px;}
  .wmd-tabs{display:flex;border-bottom:1px solid #3a3222;}
  .wmd-tab{flex:1;background:none;border:none;color:#b3a98c;font-family:inherit;font-size:28px;
    padding:12px 6px;cursor:pointer;border-bottom:3px solid transparent;}
  .wmd-tab:hover{color:#e8e0cc;}
  .wmd-tab--active{color:#ffe9a8;border-bottom-color:#ffd56b;}
  .wmd-filters{display:flex;flex-wrap:wrap;gap:8px;padding:12px 14px;border-bottom:1px solid #3a3222;}
  .wmd-chip{display:flex;align-items:center;gap:8px;background:#221e15;border:1px solid #4a4231;
    color:#8f866c;border-radius:16px;padding:6px 14px;font-family:inherit;font-size:24px;cursor:pointer;}
  .wmd-chip--on{color:#e8e0cc;border-color:#8a7a4a;background:#2c2718;}
  .wmd-chip-sw{width:16px;height:16px;border-radius:50%;opacity:0.35;}
  .wmd-chip--on .wmd-chip-sw{opacity:1;}
  .wmd-body{flex:1;overflow-y:auto;padding:10px;min-height:0;}
  .wmd-row{display:flex;align-items:center;gap:14px;padding:11px 14px;border-radius:8px;cursor:pointer;}
  .wmd-row:hover{background:#241f16;}
  .wmd-row-dot{width:18px;height:18px;border-radius:50%;flex:0 0 auto;}
  .wmd-row-ic{width:32px;text-align:center;flex:0 0 auto;font-size:28px;}
  .wmd-row-main{min-width:0;flex:1;}
  .wmd-row-name{font-size:28px;color:#e8e0cc;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .wmd-row-sub{font-size:22px;color:#8f866c;}
  .wmd-qrow{padding:12px 14px;border-radius:8px;cursor:pointer;border:1px solid transparent;margin-bottom:6px;}
  .wmd-qrow:hover{background:#241f16;}
  .wmd-qrow--active{border-color:#ffd56b;background:#2a2416;}
  .wmd-qrow-name{font-size:28px;color:#ffe9a8;margin-bottom:5px;}
  .wmd-qobj{font-size:24px;color:#c9c0a6;padding-left:10px;}
  .wmd-qobj--done{color:#77dd77;}
  /* Catálogo de quests (tab Quests rediseñado): filtros + filas con chip de nivel. */
  .wmd-qtoggles{display:flex;gap:10px;padding:0 10px 10px;}
  .wmd-qtoggle{flex:1;background:#221e15;border:1px solid #4a4231;color:#8f866c;border-radius:16px;
    padding:7px 12px;font-family:inherit;font-size:22px;cursor:pointer;}
  .wmd-qtoggle:hover{color:#e8e0cc;}
  .wmd-qtoggle--on{color:#14130f;background:#ffd56b;border-color:#ffd56b;font-weight:bold;}
  .wmd-qlist{display:flex;flex-direction:column;gap:6px;}
  .wmd-qrow2{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:8px;
    cursor:pointer;border:1px solid transparent;}
  .wmd-qrow2:hover{background:#241f16;border-color:#4a4231;}
  .wmd-qrow2-lvl{flex:0 0 auto;min-width:64px;text-align:center;font-size:22px;color:#ffe9a8;
    background:#2c2718;border:1px solid #4a4231;border-radius:6px;padding:4px 8px;}
  .wmd-qrow2-main{min-width:0;flex:1;}
  .wmd-qrow2-name{font-size:26px;color:#e8e0cc;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .wmd-qrow2-sub{font-size:20px;color:#8f866c;}
  /* No llegás de nivel → todo más oscuro. */
  .wmd-qrow2--locked .wmd-qrow2-name{color:#6f684f;}
  .wmd-qrow2--locked .wmd-qrow2-lvl{color:#8a7f56;background:#1c190f;}
  .wmd-qrow2--locked .wmd-qrow2-sub{color:#5c563f;}
  /* Completada → atenuada. */
  .wmd-qrow2--done .wmd-qrow2-name{color:#8f9c7a;}
  .wmd-qrow2--done .wmd-qrow2-sub{color:#77dd77;}
  .wmd-qrow2--done .wmd-qrow2-lvl{color:#77964f;}
  .wmd-hint,.wmd-empty{font-size:24px;color:#8f866c;padding:16px 12px;text-align:center;}
  .wmd-search{margin:10px 10px 12px;}
  .wmd-results{display:flex;flex-direction:column;}
  .wmd-markers{flex:0 0 auto;border-top:2px solid #3a3222;background:rgba(10,9,7,0.6);
    display:flex;flex-direction:column;max-height:34vh;}
  .wmd-markers-head{display:flex;align-items:center;justify-content:space-between;
    padding:12px 16px 8px;color:#ffe9a8;font-size:26px;letter-spacing:1px;}
  .wmd-mk-create{background:#2c2718;border:1px solid #8a7a4a;color:#ffd400;font-family:inherit;
    font-size:22px;padding:5px 14px;border-radius:8px;cursor:pointer;}
  .wmd-mk-create:hover{color:#ffffff;border-color:#ffd56b;}
  .wmd-markers-list{overflow-y:auto;padding:4px 8px 10px;min-height:0;}
  .wmd-mk-row{display:flex;align-items:center;gap:12px;padding:9px 10px;border-radius:8px;}
  .wmd-mk-row:hover{background:#241f16;}
  .wmd-mk-dot{width:18px;height:18px;border-radius:4px;flex:0 0 auto;border:1px solid rgba(0,0,0,0.6);}
  .wmd-mk-main{min-width:0;flex:1;cursor:pointer;}
  .wmd-mk-name{font-size:26px;color:#e8e0cc;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .wmd-mk-sub{font-size:20px;color:#8f866c;}
  .wmd-mk-btn{background:none;border:none;color:#cbb78a;font-family:inherit;font-size:26px;
    cursor:pointer;padding:2px 8px;}
  .wmd-mk-btn:hover{color:#ffffff;}
  .wmd-mk-btn--del:hover{color:#ff5555;}
  .wmd-mk-empty{font-size:22px;color:#8f866c;padding:12px;text-align:center;}
  `;
  document.head.appendChild(style);
}
